import { InjectionToken } from "@angular/core";
import { Action, ActionReducerMap, createSelector } from "@ngrx/store";

import * as fromSkills from "./skill/reducers/skill.reducer";
import * as fromSkillTypes from "./skill-type/reducers/skill-types.reducer";
import * as fromSkillAliases from "./skill-alias/reducers/skill-alias.reducer";
import * as fromUsers from "./consultant-skill/reducers/consultant.reducer";
import * as fromNotification from "./notifications/reducers/notifications.reducer";
import * as fromTeams from "./build-team/reducers/build-team.reducer";
import * as fromThingSpace from "./thingspace/reducers/thingspace.reducer";
import * as fromSkillGroups from "./skill-groups/reducers/skill-groups.reducer";
import * as fromReleaseNotes from "./release-notes/reducers/release-notes.reducer";
import * as fromShownReleaseNotes from "./release-notes/reducers/shown-release-note.reducer";
import * as fromFilteredConsultants from "./consultant-skill/reducers/consultant-search.reducer";
import * as fromCapabilities from "./capabilities/reducers/capabilities.reducer";
import * as fromSkillChampions from "./thingspace/reducers/skill-champion.reducer";
import * as fromSkillStatistics from "./skill-statistics/reducers/skill-statistics.reducer";
import * as fromCertifications from "./certifications/reducers/certifications.reducer";
import * as fromCapabilitiesViz from "./capabilities-viz/reducers/capabilities-viz.reducer";
import * as fromIndustry from "./industry/reducers/industry.reducer";
import * as fromUserSearchBarState from "./release-notes/reducers/user-search-bar-reducer";

import { ISkillNode } from "./models/skillNode";
import ISkillNodeWithRating from "./models/skillNodeWithRating";

export interface AppState {
  skills: fromSkills.ISkillsState;
  skillTypes: fromSkillTypes.ISkillTypesState;
  skillAliases: fromSkillAliases.ISkillAliasesState;
  users: fromUsers.IUsersState;
  notifications: fromNotification.INotificationState;
  teams: fromTeams.ITeamState;
  thingSpace: fromThingSpace.IThingSpaceState;
  releaseNotes: fromReleaseNotes.IReleaseNotesState;
  shownReleaseNotes: fromShownReleaseNotes.State;
  filteredConsultants: fromFilteredConsultants.IConsultantSearchState;
  capabilities: fromCapabilities.ICapabilitiesState;
  skillChampions: fromSkillChampions.ISkillChampionsState;
  skillStatistics: fromSkillStatistics.ISkillStatisticsState;
  certifications: fromCertifications.ICertificationsState;
  capabilitiesviz: fromCapabilitiesViz.ICapabilitiesVizState;
  skillGroups: fromSkillGroups.ISkillGroupsState;
  industry: fromIndustry.IIndustryState;
  filteredUsers: fromUserSearchBarState.IUserSearchBarState;
}

export const reducers = new InjectionToken<ActionReducerMap<AppState, Action>>(
  "Root reducers token",
  {
    factory: () => ({
      skills: fromSkills.reducer,
      skillTypes: fromSkillTypes.reducer,
      skillAliases: fromSkillAliases.reducer,
      users: fromUsers.reducer,
      notifications: fromNotification.reducer,
      teams: fromTeams.reducer,
      thingSpace: fromThingSpace.reducer,
      releaseNotes: fromReleaseNotes.reducer,
      shownReleaseNotes: fromShownReleaseNotes.reducer,
      filteredConsultants: fromFilteredConsultants.reducer,
      capabilities: fromCapabilities.reducer,
      skillChampions: fromSkillChampions.reducer,
      skillGroups: fromSkillGroups.reducer,
      skillStatistics: fromSkillStatistics.reducer,
      certifications: fromCertifications.reducer,
      capabilitiesviz: fromCapabilitiesViz.reducer,
      industry: fromIndustry.reducer,
      filteredUsers: fromUserSearchBarState.reducer,
    }),
  },
);

export const getSkillsSelectedUserDoesntHave = createSelector(
  fromSkills.getSkillNodes,
  fromUsers.getSelectedUserSkills,
  (allSkills, usersSkills) =>
    allSkills.filter(
      (skill) => !usersSkills.find((userSkill) => userSkill.Id === skill.Id),
    ),
);

export const getPotentialSkillAliases = createSelector(
  fromSkillAliases.getSelectedParentSkill,
  fromSkillAliases.getTemporarySkillAliases,
  fromSkills.getSkillNodes,
  (parentSkill, temporarySkillAliases, allSkills) =>
    allSkills.filter(
      (skill) =>
        skill !== parentSkill &&
        !temporarySkillAliases.some((alias) => alias.Id === skill.Id),
    ),
);

export const getIsUsersLoading = createSelector(
  fromUsers.getConsultantLoading,
  (isLoading) => Object.values(isLoading).some((value) => value),
);

export const getIsSkillsAliasLoading = createSelector(
  fromSkillAliases.getSkillAliasLoading,
  (isLoading) => Object.values(isLoading).some((value) => value),
);

export const getIsSkillsLoading = createSelector(
  fromSkills.getSkillLoading,
  (isLoading) => Object.values(isLoading).some((value) => value),
);

export const getIsSkillTypeLoading = createSelector(
  fromSkillTypes.getSkillTypeLoading,
  (isLoading) => Object.values(isLoading).some((value) => value),
);

export const getIsThingSpaceLoading = createSelector(
  fromThingSpace.getThingSpaceLoading,
  (isLoading) => Object.values(isLoading).some((value) => value),
);

export const getIsTeamLoading = createSelector(
  fromTeams.getTeamLoading,
  (isLoading) => Object.values(isLoading).some((value) => value),
);

export const getIsReleaseNotesLoading = createSelector(
  fromReleaseNotes.getReleaseNoteLoading,
  (isLoading) => Object.values(isLoading).some((value) => value),
);

export const getIsAnyLoading = createSelector(
  getIsUsersLoading,
  getIsSkillsAliasLoading,
  getIsSkillsLoading,
  getIsSkillTypeLoading,
  getIsThingSpaceLoading,
  getIsTeamLoading,
  getIsReleaseNotesLoading,
  (
    isUsersLoading,
    isSkillsAliasLoading,
    isSkillsLoading,
    isSkillTypeLoading,
    isThingSpaceLoading,
    isTeamLoading,
    isReleaseNoteLoading,
  ) =>
    isUsersLoading ||
    isSkillsAliasLoading ||
    isSkillsLoading ||
    isSkillTypeLoading ||
    isThingSpaceLoading ||
    isTeamLoading ||
    isReleaseNoteLoading,
);

export const skillsUserDoesntHave = createSelector(
  fromSkills.getSkillNodes,
  fromUsers.getSelectedUserSkills,
  (allSkills: ISkillNode[], userSkills: ISkillNodeWithRating[]) =>
    allSkills.filter(
      (skill) => !userSkills.find((skillNode) => skillNode.Id === skill.Id),
    ),
);
